import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createShift, updateShift, deleteShift } from '../services/api.ts';
import { useQueryClient } from '@tanstack/react-query';
import { useEmployees, useShiftsInRange, writeCachedRows } from '../data/queries.ts';
import { queryKeys } from '../data/queryKeys.ts';
import { matchesNameQuery } from '../utils/nameFilter.ts';
import { useAuth } from '../contexts/AuthContext.tsx';
import { localDateToUtcStart, localDateToUtcEnd } from '../utils/timezone.ts';
import { NAMEPLATE_COLORS, crnaNameplateColor, doctorBullpenColor } from './staffNameplates.ts';
import {
  weekDays, monthGrid, mondayOf, shiftsByEmployeeDay, cellFor, dayTotals,
  parseCellInput, utcFromLocal, windowCrossesMidnight, singleDay, sectionTally,
  GridDay, GridShift, GridCell,
} from './staffingGrid.ts';

/**
 * The Manpower spreadsheet: every anesthesiologist and CRNA in the SYSTEM down the left, days across.
 *
 * This inverts what the old Available Shifts view did. That was a projection of shift rows, so a person
 * with no shift simply did not appear. Here the ROSTER is the row set and shifts fill cells in, which is
 * what makes an empty cell meaningful -- it is a day you can staff, not an absence of data.
 *
 * Sections are stacked (anesthesiologists, then CRNAs) mirroring the "By group" view on the staff tabs,
 * rather than side by side.
 *
 * Admins edit a cell in place (commitCell) through update-shift / delete-shift / create-shift. Before
 * those handlers the only shift writes were create-shift, the bulk importers, and wipeShifts(day) --
 * which deletes the whole day for everyone.
 */

const WEEK_OR_MONTH_KEY = 'oc-manpower-grid-span';

export type GridSpan = 'day' | 'week' | 'month';
const SPANS: readonly GridSpan[] = ['day', 'week', 'month'];
/** Whitelists the stored value -- an unknown or stale string falls back rather than passing through. */
const loadSpan = (): GridSpan => {
  try {
    const v = localStorage.getItem(WEEK_OR_MONTH_KEY) as GridSpan | null;
    return v && SPANS.includes(v) ? v : 'week';
  } catch { return 'week'; }
};
const saveSpan = (s: GridSpan) => {
  try { localStorage.setItem(WEEK_OR_MONTH_KEY, s); } catch { /* private mode */ }
};

const chip = (bg: string, fg = '#fff'): React.CSSProperties => ({
  fontSize: '0.7em', fontWeight: 600, padding: '2px 7px', borderRadius: '999px',
  background: bg, color: fg, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums',
});
const tallyCell: React.CSSProperties = {
  position: 'sticky', bottom: 0, background: 'var(--bg-surface)',
  borderTop: '2px solid var(--border-subtle, var(--text-faint))',
};
const th: React.CSSProperties = {
  textAlign: 'left', fontSize: '0.64em', textTransform: 'uppercase', letterSpacing: '0.4px',
  color: 'var(--text-faint)', fontWeight: 800, padding: '5px 8px', whiteSpace: 'nowrap',
  background: 'var(--bg-surface)', position: 'sticky', top: 0, zIndex: 2,
};
const nameCell: React.CSSProperties = {
  padding: '3px 8px', fontSize: '0.8em', whiteSpace: 'nowrap', fontWeight: 600,
  position: 'sticky', left: 0, background: 'var(--bg-surface)', zIndex: 1,
  borderRight: '1px solid var(--border-subtle, var(--text-faint))',
};
const dayCell: React.CSSProperties = {
  padding: '3px 6px', fontSize: '0.78em', textAlign: 'center', whiteSpace: 'nowrap',
  fontVariantNumeric: 'tabular-nums', borderLeft: '1px solid var(--tint-zebra, rgba(127,127,127,0.06))',
};

/** Section bar, matching renderGroupHeader on the CRNA tab: colour bar, title left, count right. */
const SectionBar: React.FC<{ title: string; count: number; color: string }> = ({ title, count, color }) => (
  <div style={{
    background: color, color: '#fff', fontWeight: 700, fontSize: '0.74em', letterSpacing: '0.4px',
    textTransform: 'uppercase', padding: '5px 10px', display: 'flex', justifyContent: 'space-between',
  }}>
    <span>{title}</span><span style={{ opacity: 0.9 }}>{count}</span>
  </div>
);

type Emp = { employeeId: string; firstName: string; lastName: string; employeeType: string };

const EMPTY_EMPS: Emp[] = [];
const EMPTY_SHIFTS: GridShift[] = [];

const StaffingGrid: React.FC<{ selectedDate: string; nameFilter?: string; refreshKey?: number }> = ({
  selectedDate, nameFilter = '', refreshKey,
}) => {
  const [span, setSpan] = useState<GridSpan>(loadSpan);
  const [anchor, setAnchor] = useState(selectedDate);
  // Editing state lives HERE, in the parent -- see renderCell's note on element identity.
  const [editKey, setEditKey] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [cellError, setCellError] = useState<{ key: string; msg: string } | null>(null);
  const { isAdmin } = useAuth();

  useEffect(() => { setAnchor(selectedDate); }, [selectedDate]);

  // The fetch window covers every column on screen, so one request serves the whole grid.
  const columns: GridDay[] = useMemo(
    () => (span === 'day' ? singleDay(anchor) : span === 'week' ? weekDays(anchor) : monthGrid(anchor).flat()),
    [span, anchor],
  );

  // One cached entry per on-screen WINDOW (data/queries.ts). Keying by the window is what keeps a slow
  // response for the previous week from landing under this week's columns: it can only ever fill its own
  // entry. (Applied to the wrong columns, every cell would read "not rostered" -- and because the grid is
  // EDITABLE, typing into one of those falsely empty cells would create a duplicate shift.)
  const fromUtc = columns.length ? localDateToUtcStart(columns[0].date) : '';
  const toUtc = columns.length ? localDateToUtcEnd(columns[columns.length - 1].date) : '';
  const employeesQuery = useEmployees();
  const shiftsQuery = useShiftsInRange(fromUtc, toUtc);
  const employees = (employeesQuery.data ?? EMPTY_EMPS) as Emp[];
  const shifts = (shiftsQuery.data ?? EMPTY_SHIFTS) as GridShift[];
  const loading = employeesQuery.isPending || shiftsQuery.isPending;
  const failed = (employeesQuery.isError && !employeesQuery.data) ? employeesQuery.error : (shiftsQuery.isError && !shiftsQuery.data) ? shiftsQuery.error : null;
  const error = failed ? ((failed as any)?.response?.data?.error || 'Could not load the staffing grid.') : '';
  const queryClient = useQueryClient();
  // Patch the on-screen window after a successful write (never before: the grid must not show a value the
  // database refused). api.ts also marks shifts stale, so the window revalidates quietly afterwards.
  const setShifts = (next: (prev: GridShift[]) => GridShift[]) =>
    writeCachedRows<GridShift>(queryClient, queryKeys.shiftsInRange(fromUtc, toUtc), next);
  const { refetch: refetchShifts } = shiftsQuery;
  const firstRefreshKey = useRef(refreshKey);
  useEffect(() => {
    if (refreshKey !== firstRefreshKey.current) refetchShifts();
  }, [refreshKey, refetchShifts]);

  const byEmployeeDay = useMemo(() => shiftsByEmployeeDay(shifts), [shifts]);
  const typeById = useMemo(() => {
    const m = new Map<string, string>();
    for (const e of employees) m.set(e.employeeId, e.employeeType);
    return m;
  }, [employees]);

  // Students are deliberately out: Braden asked for every doc and CRNA. They have no pecking order and
  // are rostered differently, so a third section would be noise here.
  const roster = useMemo(() => {
    const keep = (e: Emp) => matchesNameQuery(e, nameFilter);
    const byName = (a: Emp, b: Emp) =>
      `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`);
    return {
      doctors: employees.filter(e => e.employeeType === 'doctor').filter(keep).sort(byName),
      crnas: employees.filter(e => e.employeeType === 'nurse').filter(keep).sort(byName),
    };
  }, [employees, nameFilter]);

  const colorFor = (cell: GridCell, type: string): string | undefined => {
    if (cell.kind === 'off') return undefined;
    if (cell.kind === 'pecking') {
      return type === 'doctor' ? NAMEPLATE_COLORS.peckingDoctor : NAMEPLATE_COLORS.peckingCrna;
    }
    const ns = { startTimeUtc: cell.shift?.startTimeUtc, endTimeUtc: cell.shift?.endTimeUtc, order: null };
    return type === 'doctor' ? doctorBullpenColor(ns) : crnaNameplateColor(ns);
  };

  /**
   * Apply a typed cell value. Optimistic ONLY on success: the local shifts array is patched from the
   * row the handler returns, never on failure, so the grid can never show a value the database
   * refused. A week refetch would rebuild 488 rows and flash.
   */
  const commitCell = async (emp: Emp, day: GridDay, existing: GridCell, raw: string) => {
    const key = `${emp.employeeId}|${day.date}`;
    const parsed = parseCellInput(raw);
    if (parsed.kind === 'invalid') { setCellError({ key, msg: parsed.reason }); return; }
    const shiftId = (existing.shift as any)?.shiftId as string | undefined;
    setSaving(true); setCellError(null);
    try {
      if (parsed.kind === 'clear') {
        if (!shiftId) { setEditKey(null); return; }
        if (existing.kind === 'pecking') {
          const res = await updateShift(shiftId, { order: null });
          setShifts(prev => prev.map(x => ((x as any).shiftId === shiftId ? res.shift : x)));
        } else {
          await deleteShift(shiftId);
          setShifts(prev => prev.filter(x => (x as any).shiftId !== shiftId));
        }
      } else if (parsed.kind === 'pecking') {
        if (!shiftId) {
          setCellError({ key, msg: 'Give this day a shift window first (e.g. 0700-1500), then a number' });
          return;
        }
        const res = await updateShift(shiftId, { order: parsed.order });
        setShifts(prev => prev.map(x => ((x as any).shiftId === shiftId ? res.shift : x)));
      } else {
        const over = windowCrossesMidnight(parsed.startHHMM, parsed.endHHMM);
        const startTimeUtc = utcFromLocal(day.date, parsed.startHHMM);
        const endTimeUtc = utcFromLocal(day.date, parsed.endHHMM, over);
        if (!startTimeUtc || !endTimeUtc) { setCellError({ key, msg: 'Could not read those times' }); return; }
        if (shiftId) {
          const res = await updateShift(shiftId, { startTimeUtc, endTimeUtc });
          setShifts(prev => prev.map(x => ((x as any).shiftId === shiftId ? res.shift : x)));
        } else {
          const res = await createShift({
            employeeId: emp.employeeId, employeeType: emp.employeeType as any, startTimeUtc, endTimeUtc,
          } as any);
          setShifts(prev => [...prev, res.shift ?? res]);
        }
      }
      setEditKey(null); setDraft('');
    } catch (e: any) {
      setCellError({ key, msg: e?.response?.data?.error || 'Save failed' });
    } finally { setSaving(false); }
  };

  /**
   * One cell. A PLAIN FUNCTION, not a component declared in the body: `const Cell: React.FC = ...`
   * inside a component creates a NEW component type every render, so React remounts the subtree and an
   * open input loses focus on every keystroke. The staff tabs hit this and note it twice.
   */
  const renderCell = (emp: Emp, day: GridDay) => {
    const cell = cellFor(byEmployeeDay.get(emp.employeeId)?.get(day.date));
    const color = colorFor(cell, emp.employeeType);
    const key = `${emp.employeeId}|${day.date}`;
    const err = cellError?.key === key ? cellError.msg : null;

    if (editKey === key) {
      return (
        <td key={day.date} style={{ ...dayCell, padding: '2px' }}>
          <input
            autoFocus value={draft} disabled={saving}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') commitCell(emp, day, cell, draft);
              if (e.key === 'Escape') { setEditKey(null); setDraft(''); setCellError(null); }
            }}
            onBlur={() => { if (!saving) { setEditKey(null); setDraft(''); } }}
            placeholder="5 or 0700-1500"
            title={err || 'Enter to save · Esc to cancel · empty to clear'}
            style={{
              font: 'inherit', fontSize: '0.86em', width: '84px', textAlign: 'center',
              padding: '1px 3px', fontVariantNumeric: 'tabular-nums',
              border: `1px solid ${err ? '#b91c1c' : 'var(--or-blue-mid)'}`, borderRadius: '4px',
              background: 'var(--bg-input, var(--bg-surface))', color: 'inherit',
            }}
          />
        </td>
      );
    }
    return (
      <td key={day.date}
        onClick={isAdmin ? () => {
          setEditKey(key);
          setDraft(cell.kind === 'pecking' ? String(cell.order ?? '') : (cell.kind === 'shift' ? cell.text : ''));
          setCellError(null);
        } : undefined}
        style={{
          ...dayCell, opacity: day.inMonth ? 1 : 0.45,
          cursor: isAdmin ? 'pointer' : 'default', outline: err ? '1px solid #b91c1c' : undefined,
        }}
        title={err || (cell.kind === 'off'
          ? `${emp.firstName} ${emp.lastName} — not rostered ${day.date}${isAdmin ? ' · click to add' : ''}`
          : `${emp.firstName} ${emp.lastName} — ${day.date}${cell.extra > 0 ? ` (+${cell.extra} more)` : ''}${isAdmin ? ' · click to edit' : ''}`)}>
        {cell.kind === 'off'
          ? <span style={{ color: 'var(--text-faint)' }}>·</span>
          : (
            <span style={{ color, fontWeight: cell.kind === 'pecking' ? 800 : 600 }}>
              {cell.text}
              {cell.extra > 0 && <sup style={{ color: 'var(--amber)', fontWeight: 800 }}>+{cell.extra}</sup>}
            </span>
          )}
      </td>
    );
  };

  // The tally is for a Day or a Week only. Across a month the window buckets would be a sum of ~30
  // days, which reads like a headcount and is not one -- per Braden, no month tally.
  const showTally = span === 'day' || span === 'week';
  const tallyFor = (rows: Emp[]) =>
    sectionTally(rows.map(e => e.employeeId), byEmployeeDay, columns.map(d => d.date));

  // Also a plain function, for the same element-identity reason as renderCell.
  const renderSection = (title: string, color: string, rows: Emp[]) => (
    <div style={{ marginBottom: '16px', borderRadius: '8px', overflow: 'hidden', border: `1px solid ${color}` }}>
      <SectionBar title={title} count={rows.length} color={color} />
      <div style={{ maxHeight: '420px', overflow: 'auto', background: 'var(--bg-surface)' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr>
              <th style={{ ...th, left: 0, zIndex: 3, minWidth: '170px' }}>Name</th>
              {columns.map(d => (
                <th key={d.date} style={{ ...th, textAlign: 'center', opacity: d.inMonth ? 1 : 0.5 }}>
                  {span === 'month' ? d.dayOfMonth : `${d.label} ${d.dayOfMonth}`}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={columns.length + 1} style={{ padding: '8px 10px', fontSize: '0.8em', color: 'var(--text-faint)' }}>
                {nameFilter.trim() ? 'Nobody matches that name.' : 'None in the system.'}
              </td></tr>
            ) : rows.map(e => (
              <tr key={e.employeeId}>
                <td style={nameCell}>{e.lastName}, {e.firstName}</td>
                {columns.map(d => renderCell(e, d))}
              </tr>
            ))}
          </tbody>
          {showTally && (
            <tfoot>
              <tr>
                <td style={{ ...nameCell, ...tallyCell, fontWeight: 800 }}>On</td>
                {columns.map((d, i) => {
                  const t = tallyFor(rows).perDate[i];
                  return (
                    <td key={d.date} style={{ ...dayCell, ...tallyCell, fontWeight: 800 }}
                      title={`${d.date}: ${t?.rostered ?? 0} rostered, ${t?.pecking ?? 0} in pecking order`}>
                      {t?.rostered ?? 0}
                      {!!t?.pecking && <sub style={{ color: NAMEPLATE_COLORS.peckingCrna, fontWeight: 800 }}>·{t.pecking}p</sub>}
                    </td>
                  );
                })}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      {showTally && (() => {
        const t = tallyFor(rows);
        return (
          <div style={{ padding: '6px 8px', borderTop: '1px solid var(--border-subtle, var(--text-faint))',
                        display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: '0.62em', fontWeight: 800, textTransform: 'uppercase',
                           letterSpacing: '0.06em', color: 'var(--text-faint)' }}>
              {span === 'day' ? 'This day' : 'This week (shift-days)'}
            </span>
            {t.windows.length === 0 && t.pecking === 0 && (
              <span style={{ fontSize: '0.74em', color: 'var(--text-faint)', fontStyle: 'italic' }}>nobody rostered</span>
            )}
            {t.windows.map(w => (
              <span key={w.label} style={chip('var(--tint-raised-strong, rgba(127,127,127,0.12))', 'inherit')}
                title={`${w.count} ${w.label} shift${w.count === 1 ? '' : 's'}`}>
                {w.label} · <strong>{w.count}</strong>
              </span>
            ))}
            {t.pecking > 0 && (
              <span style={chip(NAMEPLATE_COLORS.peckingCrna)}
                title="Counted separately from the window buckets, as the bullpen does">
                Pecking · <strong>{t.pecking}</strong>
              </span>
            )}
            <span style={{ fontSize: '0.68em', color: 'var(--text-faint)', fontVariantNumeric: 'tabular-nums' }}>
              {t.rostered} on · {t.off} off
            </span>
          </div>
        );
      })()}
    </div>
  );

  const shiftWeek = (delta: number) => {
    const base = span === 'week' ? mondayOf(anchor) : anchor;
    if (!base) return;
    const [y, m, d] = base.split('-').map(Number);
    const dt = new Date(y, m - 1, d);
    // Each span steps by its OWN unit. Without the day branch a Day view would jump a whole month.
    if (span === 'day') dt.setDate(dt.getDate() + delta);
    else if (span === 'week') dt.setDate(dt.getDate() + delta * 7);
    else dt.setMonth(dt.getMonth() + delta);
    setAnchor(`${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`);
  };

  const monthRows = useMemo(() => (span === 'month' ? monthGrid(anchor) : []), [span, anchor]);

  return (
    <div>
      <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '10px' }}>
        <div style={{ display: 'inline-flex', border: '1px solid var(--border-subtle, var(--text-faint))', borderRadius: '6px', overflow: 'hidden' }}>
          {SPANS.map(s => (
            <button key={s} type="button"
              onClick={() => {
                setSpan(s); saveSpan(s);
                // A span change can drop the column being edited; stranding an open editor on a
                // column that no longer exists would leave an input with nowhere to save to.
                setEditKey(null); setDraft(''); setCellError(null);
              }}
              style={{
                font: 'inherit', fontSize: '0.78em', padding: '3px 10px', border: 'none', cursor: 'pointer',
                background: span === s ? 'var(--or-blue-mid)' : 'transparent',
                color: span === s ? '#fff' : 'var(--text-secondary)', fontWeight: 700,
              }}>
              {s === 'day' ? 'Day' : s === 'week' ? 'Week' : 'Month'}
            </button>
          ))}
        </div>
        <button type="button" onClick={() => shiftWeek(-1)} style={{ font: 'inherit', fontSize: '0.78em', padding: '3px 8px', cursor: 'pointer' }}>‹ Prev</button>
        <button type="button" onClick={() => shiftWeek(1)} style={{ font: 'inherit', fontSize: '0.78em', padding: '3px 8px', cursor: 'pointer' }}>Next ›</button>
        <span style={{ fontSize: '0.78em', color: 'var(--text-muted)', fontVariantNumeric: 'tabular-nums' }}>
          {columns.length > 0 && `${columns[0].date} → ${columns[columns.length - 1].date}`}
        </span>
        <span style={{ fontSize: '0.68em', color: 'var(--text-faint)' }}>
          {isAdmin ? 'click a cell to edit · 5 = pecking, 0700-1500 = window, empty = clear' : 'read-only'}
        </span>
      </div>

      {loading && <div style={{ color: 'var(--text-muted)', fontSize: '0.85em' }}>Loading…</div>}
      {error && <div style={{ color: '#dc2626', fontSize: '0.85em' }}>{error}</div>}

      {!loading && !error && span === 'month' && (
        <div style={{ marginBottom: '14px', overflowX: 'auto' }}>
          {/* Month calendar: click a day to drop into that week. Counts are who is ROSTERED that day. */}
          <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: '640px' }}>
            <thead><tr>{['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map(l => (
              <th key={l} style={{ ...th, position: 'static', textAlign: 'center' }}>{l}</th>
            ))}</tr></thead>
            <tbody>
              {monthRows.map((row, i) => (
                <tr key={i}>
                  {row.map(d => {
                    const t = dayTotals(byEmployeeDay, id => typeById.get(id), d.date);
                    return (
                      <td key={d.date}
                        onClick={() => { setSpan('week'); saveSpan('week'); setAnchor(d.date); }}
                        title={`${d.date} — ${t.doctors} docs, ${t.crnas} CRNAs rostered. Click for the week.`}
                        style={{
                          border: '1px solid var(--border-subtle, var(--text-faint))', padding: '4px 6px',
                          verticalAlign: 'top', height: '52px', cursor: 'pointer',
                          opacity: d.inMonth ? 1 : 0.4, fontVariantNumeric: 'tabular-nums',
                        }}>
                        <div style={{ fontSize: '0.74em', fontWeight: 700, color: 'var(--text-secondary)' }}>{d.dayOfMonth}</div>
                        {(t.doctors > 0 || t.crnas > 0) && (
                          <div style={{ fontSize: '0.68em', marginTop: '2px' }}>
                            <span style={{ color: NAMEPLATE_COLORS.peckingDoctor, fontWeight: 700 }}>{t.doctors}D</span>
                            {' '}
                            <span style={{ color: NAMEPLATE_COLORS.peckingCrna, fontWeight: 700 }}>{t.crnas}C</span>
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && !error && (
        <>
          {renderSection('Anesthesiologists', NAMEPLATE_COLORS.peckingDoctor, roster.doctors)}
          {renderSection('CRNAs', NAMEPLATE_COLORS.peckingCrna, roster.crnas)}
          <div style={{ fontSize: '0.7em', color: 'var(--text-faint)' }}>
            Every anesthesiologist and CRNA in the system, not only those rostered. A cell shows the pecking
            number (#N) or the shift window; <span style={{ color: 'var(--text-faint)' }}>·</span> means not
            rostered. A superscript +N marks someone holding more than one shift that day.
          </div>
        </>
      )}
    </div>
  );
};

export default StaffingGrid;
